<script setup lang="ts">
// Our own controls for an <audio>/<video> element, so every player (chat attachments,
// the video overlay, parties) looks the same in every browser and uses the app's fonts:
// play/pause, previous/next in a queue, time, a seek bar showing what's buffered, mute
// and volume, and fullscreen for video (state and actions: lib/mediaState.ts).
import { onBeforeUnmount, ref } from 'vue'
import { formatClock } from '../lib/pattern'
import { useMediaState } from '../lib/mediaState'

const props = defineProps<{
  el: HTMLMediaElement | null
  kind: 'audio' | 'video'
  /** What fullscreen shows (the video with these controls); the video alone if unset. */
  frame?: HTMLElement | null
  /** Show previous and next (a queue is playing); they emit `previous` and `next`. */
  skip?: { previous: boolean; next: boolean }
}>()
const emit = defineEmits<{ previous: []; next: [] }>()

const { playing, buffered, volume, muted, known, duration, shown, pct, toggle, scrub, seek, setVolume, toggleMute } = useMediaState(() => props.el)
const fullscreen = ref(false)

const onFullscreen = () => (fullscreen.value = !!document.fullscreenElement)
document.addEventListener('fullscreenchange', onFullscreen)
onBeforeUnmount(() => document.removeEventListener('fullscreenchange', onFullscreen))

async function toggleFullscreen(): Promise<void> {
  if (document.fullscreenElement) return void (await document.exitFullscreen().catch(() => undefined))
  const target = props.frame ?? props.el
  if (target?.requestFullscreen) await target.requestFullscreen().catch(() => undefined)
  // iPhone Safari: only the video itself goes fullscreen, with the system's controls.
  else (props.el as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null)?.webkitEnterFullscreen?.()
}

/** Space toggles, arrows step 5 s, when the controls have focus. */
function onKey(e: KeyboardEvent): void {
  const m = props.el
  if (!m || (e.target as HTMLElement).tagName === 'INPUT') return
  if (e.key === ' ' || e.key === 'k') toggle()
  else if (e.key === 'ArrowRight') m.currentTime = Math.min(m.duration || Infinity, m.currentTime + 5)
  else if (e.key === 'ArrowLeft') m.currentTime = Math.max(0, m.currentTime - 5)
  else return
  e.preventDefault()
}

defineExpose({ toggle, toggleFullscreen })
</script>

<template>
  <div class="mono flex h-8 min-w-0 items-center gap-2 select-none" data-testid="media-controls" @keydown="onKey">
    <button
      v-if="skip"
      type="button"
      class="flex h-7 w-7 shrink-0 items-center justify-center opacity-80 hover:opacity-100 disabled:opacity-30"
      aria-label="Previous"
      data-testid="media-previous"
      @click="emit('previous')"
    >
      <svg viewBox="0 0 12 12" class="h-3 w-3" aria-hidden="true"><rect x="1.5" y="1.5" width="1.5" height="9" fill="currentColor" /><path d="M10.5 1.5v9L3.5 6z" fill="currentColor" /></svg>
    </button>
    <button
      type="button"
      class="flex h-7 w-7 shrink-0 items-center justify-center opacity-80 hover:opacity-100"
      :aria-label="playing ? 'Pause' : 'Play'"
      data-testid="media-toggle"
      @click="toggle"
    >
      <svg v-if="playing" viewBox="0 0 12 12" class="h-3 w-3" aria-hidden="true"><rect x="2" y="1.5" width="3" height="9" fill="currentColor" /><rect x="7" y="1.5" width="3" height="9" fill="currentColor" /></svg>
      <svg v-else viewBox="0 0 12 12" class="h-3 w-3" aria-hidden="true"><path d="M2.5 1.5v9l8-4.5z" fill="currentColor" /></svg>
    </button>
    <button
      v-if="skip"
      type="button"
      class="flex h-7 w-7 shrink-0 items-center justify-center opacity-80 hover:opacity-100 disabled:opacity-30"
      aria-label="Next"
      :disabled="!skip.next"
      data-testid="media-next"
      @click="emit('next')"
    >
      <svg viewBox="0 0 12 12" class="h-3 w-3" aria-hidden="true"><path d="M1.5 1.5v9l7-4.5z" fill="currentColor" /><rect x="9" y="1.5" width="1.5" height="9" fill="currentColor" /></svg>
    </button>
    <span class="shrink-0 tabular-nums opacity-80" data-testid="media-time">{{ formatClock(shown) }}</span>
    <input
      type="range"
      class="media-range min-w-0 flex-1"
      min="0"
      :max="known ? duration : 0"
      step="any"
      :value="shown"
      :disabled="!known"
      :style="{ '--played': pct(shown), '--buffered': pct(buffered) }"
      aria-label="Seek"
      data-testid="media-seek"
      @input="scrub"
      @change="seek"
    />
    <span class="shrink-0 tabular-nums opacity-50" data-testid="media-duration">{{ known ? formatClock(duration) : '--:--' }}</span>
    <button
      type="button"
      class="flex h-7 w-7 shrink-0 items-center justify-center opacity-80 hover:opacity-100"
      :aria-label="muted ? 'Unmute' : 'Mute'"
      data-testid="media-mute"
      @click="toggleMute"
    >
      <svg viewBox="0 0 12 12" class="h-3 w-3" aria-hidden="true">
        <path d="M1 4.5h2.5L6.5 2v8L3.5 7.5H1z" fill="currentColor" />
        <template v-if="muted || volume === 0"><path d="M8 4.5l3 3M11 4.5l-3 3" stroke="currentColor" stroke-width="1.2" /></template>
        <template v-else><path d="M8.2 4.2a2.5 2.5 0 0 1 0 3.6" fill="none" stroke="currentColor" stroke-width="1.2" /><path v-if="volume > 0.5" d="M9.6 2.8a4.5 4.5 0 0 1 0 6.4" fill="none" stroke="currentColor" stroke-width="1.2" /></template>
      </svg>
    </button>
    <input
      type="range"
      class="media-range w-16 shrink-0 max-sm:hidden"
      min="0"
      max="1"
      step="0.05"
      :value="muted ? 0 : volume"
      :style="{ '--played': `${(muted ? 0 : volume) * 100}%`, '--buffered': '0%' }"
      aria-label="Volume"
      data-testid="media-volume"
      @input="setVolume"
    />
    <button
      v-if="kind === 'video'"
      type="button"
      class="flex h-7 w-7 shrink-0 items-center justify-center opacity-80 hover:opacity-100"
      :aria-label="fullscreen ? 'Exit full screen' : 'Full screen'"
      data-testid="media-fullscreen"
      @click="toggleFullscreen"
    >
      <svg viewBox="0 0 12 12" class="h-3 w-3" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.2">
        <path v-if="fullscreen" d="M4 1v3H1M8 1v3h3M4 11V8H1M8 11V8h3" />
        <path v-else d="M1 4V1h3M8 1h3v3M1 8v3h3M11 8v3H8" />
      </svg>
    </button>
  </div>
</template>
